import superAdminApi from "./superAdminApi";
import CommonService from "./common.service";
import SuperAdminStorageService from "./SuperAdminStorageService";
import { isLogisticsManagerUser, isLogisticsStaffUser } from "../utils/roleSwitcher";
import { getLogisticsDedicatedSubmittedRfqPath } from "../utils/logisticsRfqPostSubmitNavigate";

const isLogisticsManagerPortal = () =>
  typeof window !== "undefined" &&
  window.location.pathname.startsWith("/dashboard/logistics-manager");

export const extractApiMessage = (err, fallback = "Request failed") =>
  err?.response?.data?.message || err?.response?.data?.error || err?.message || fallback;

export async function checkBusinessRelocationDuplicate(form) {
  const payload = {
    relocationScope: form.relocationScope,
    currentAddress: form.currentAddress,
    destAddress: form.destAddress,
    preferredMoveDate: form.preferredMoveDate,
  };
  const res = await CommonService.post(
    "/logistics/business-relocation-rfq/check-duplicate",
    payload,
    { silent: true },
  );
  return res?.data ?? res ?? { hasDuplicate: false, existingRfq: null };
}

export async function submitBusinessRelocationRfq(form, options = {}) {
  const { confirmDuplicateSubmit = false, clientRequestId = null } = options;
  const payload = {
    rfqReferenceNumber: form.rfqReferenceNumber,
    companyId: form.companyId ?? null,
    contactPerson: form.contactPerson,
    contactNumber: form.contactNumber,
    companyName: form.companyName,
    companyEmail: form.companyEmail,
    relocationScope: form.relocationScope,
    currentCountry: form.currentCountry,
    currentState: form.currentState,
    currentCity: form.currentCity,
    currentPincode: form.currentPincode,
    currentAddress: form.currentAddress,
    destCountry: form.destCountry,
    destState: form.destState,
    destCity: form.destCity,
    destPincode: form.destPincode,
    destAddress: form.destAddress,
    supplementalLocations: form.supplementalLocations ?? { current: [], destination: [] },
    totalWeightKg: form.totalWeightKg,
    totalWeightUnit: form.totalWeightUnit || 'KG',
    totalPackages: form.totalPackages,
    totalValueUsd: form.totalValueUsd,
    totalValueCurrency: form.totalValueCurrency || 'USD',
    preferredMoveDate: form.preferredMoveDate,
    urgencyLevel: form.urgencyLevel,
    logisticsMode: form.logisticsMode,
    budgetRange: form.budgetRange,
    specialNotes: form.specialNotes,
    additionalServices: form.additionalServices,
    confirmDuplicateSubmit: confirmDuplicateSubmit || undefined,
    clientRequestId: clientRequestId || undefined,
  };

  const res = await CommonService.post("/logistics/business-relocation-rfq", payload);
  return res;
}

export async function fetchBusinessRelocationRfqs(params = {}) {
  if (isLogisticsManagerPortal()) {
    const res = await CommonService.get("/logistics-manager/business-relocation-rfqs", params, {
      silent: true,
    });
    return res?.data ?? { items: [], total: 0, page: 1, pageSize: 20 };
  }
  const res = await superAdminApi.get("/super-admin/logistics/business-relocation-rfqs", { params });
  return res.data?.data ?? { items: [], total: 0, page: 1, pageSize: 20 };
}

export async function fetchBusinessRelocationRfqStats(params = {}) {
  if (isLogisticsManagerPortal()) {
    const res = await CommonService.get("/logistics-manager/business-relocation-rfqs/stats", params, {
      silent: true,
    });
    return (
      res?.data ?? {
        total: 0,
        assigned: 0,
        inProgress: 0,
        completed: 0,
        overdue: 0,
        thisMonth: 0,
      }
    );
  }
  const res = await superAdminApi.get("/super-admin/logistics/business-relocation-rfqs/stats", {
    params,
  });
  return res.data?.data ?? {
    total: 0,
    assigned: 0,
    inProgress: 0,
    completed: 0,
    overdue: 0,
    thisMonth: 0,
  };
}

export async function fetchBusinessRelocationRfqCities() {
  if (isLogisticsManagerPortal()) {
    const res = await CommonService.get(
      "/logistics-manager/business-relocation-rfqs/cities",
      {},
      { silent: true },
    );
    return res?.data ?? { cities: [] };
  }
  const res = await superAdminApi.get("/super-admin/logistics/business-relocation-rfqs/cities");
  return res.data?.data ?? { cities: [] };
}

export async function fetchBusinessRelocationRfqById(id) {
  if (isLogisticsManagerPortal()) {
    const res = await CommonService.get(`/logistics-manager/business-relocation-rfqs/${id}`, {}, {
      silent: true,
    });
    return res?.data ?? { rfq: null };
  }
  const res = await superAdminApi.get(`/super-admin/logistics/business-relocation-rfqs/${id}`);
  return res.data?.data ?? { rfq: null };
}

export function businessRelocationSubmittedRfqPath(id, options = {}) {
  return getLogisticsDedicatedSubmittedRfqPath("business-relocation", id, options);
}

export async function fetchBusinessRelocationRfqForSubmittedView(id) {
  if (SuperAdminStorageService.isAuthenticated()) {
    const data = await fetchBusinessRelocationRfqById(id);
    return data?.rfq ?? null;
  }
  if (isLogisticsStaffUser()) {
    const res = await CommonService.get(
      `/logistics-manager/business-relocation-rfqs/${id}`,
      {},
      { silent: true },
    );
    const body = res?.data ?? res;
    return body?.rfq ?? (body && typeof body === "object" ? body : null);
  }
  const res = await CommonService.get(`/logistics/business-relocation-rfq/my/${id}`);
  const body = res?.data?.data ?? res?.data ?? res;
  const rfq = body?.rfq ?? body;
  return rfq && typeof rfq === "object" ? rfq : null;
}
